// Account settings components: currently just the setting for password
// (or passwordless-login option), implemented as a state hook and component
// that plug directly in to the login component, and a standalone wrapper
// component for changing settings once logged in.

import React, { useState, useEffect, useMemo, Fragment } from "react";

import {
  Content,
  Form,
  Container,
  Message,
  Button,
} from "react-bulma-components";

import TrimmedInput from "common-frontend/util/trimmedInput";
import { useSetTitle } from "util/title";
import { useReturn } from "util/return";
import { useSelector, useDispatch } from "react-redux";
import { profileSlice } from "redux/features/profile";
import { changePasswordSlice } from "redux/features/account";
import { usePortalContext } from "context";
import ErrorMessage from "util/errorMessage";
import { FaTimes, FaCheck } from "react-icons/fa";
import IconButton from "util/iconButton";

const { Field, Control, Label, Checkbox, Radio, Help } = Form;

export function usePromptNewPasswordState(haveExisting = {}) {
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [newPasswordEntered, setNewPasswordEntered] = useState(false);
  const [newPasswordConfirmed, setNewPasswordConfirmed] = useState(false);

  const [noPassword, setNoPassword] = useState(haveExisting.noPassword);

  const newPasswordIncomplete =
    !noPassword && newPassword === "" && !haveExisting.password
      ? "Please enter the password you want to use on future sign-ins"
      : null;
  const newPasswordError = newPasswordEntered ? newPasswordIncomplete : null;

  const newPasswordConfirmationIncomplete =
    newPassword !== "" && newPasswordConfirmation === ""
      ? "Please confirm your new password by entering it in both boxes"
      : null;

  const newPasswordMismatch =
    !noPassword &&
    !newPasswordIncomplete &&
    !newPasswordConfirmationIncomplete &&
    newPassword !== newPasswordConfirmation
      ? "Your new passwords do not match"
      : null;

  const newPasswordConfirmationError =
    newPasswordConfirmed && !newPasswordError
      ? newPasswordConfirmationIncomplete ??
        (newPasswordEntered && newPasswordMismatch ? newPasswordMismatch : null)
      : null;

  const promptNewPasswordState = useMemo(() => {
    return {
      newPassword,
      setNewPassword,
      newPasswordConfirmation,
      setNewPasswordConfirmation,
      noPassword,
      setNoPassword,
      setNewPasswordEntered,
      setNewPasswordConfirmed,
      newPasswordIncomplete,
      newPasswordError,
      newPasswordConfirmationIncomplete,
      newPasswordConfirmationError,
      newPasswordMismatch,
      newPasswordLoginData: {
        new_password: newPassword,
        disable_password: noPassword,
      },
    };
  }, [
    newPassword,
    newPasswordConfirmation,
    newPasswordIncomplete,
    newPasswordError,
    newPasswordConfirmationIncomplete,
    newPasswordConfirmationError,
    newPasswordMismatch,
    noPassword,
  ]);

  return promptNewPasswordState;
}

export function PromptNewPassword({
  loggingIn,
  resettingPassword,
  haveExisting,
  newPassword,
  setNewPassword,
  newPasswordConfirmation,
  setNewPasswordConfirmation,
  noPassword,
  setNoPassword,
  newPasswordError,
  newPasswordConfirmationError,
  setNewPasswordEntered,
  setNewPasswordConfirmed,
}) {
  const setNoPasswordWithClearing = (value) => {
    if (value) {
      setNewPassword("");
      setNewPasswordConfirmation("");
      setNewPasswordEntered(false);
      setNewPasswordConfirmed(false);
    }
    setNoPassword(value);
  };

  const existing = haveExisting || {};
  return (
    <div className="other-login-data-prompt">
      <Content>
        <Fragment>
          {resettingPassword ? (
            <p>
              To complete the password reset process, please choose a new
              password and enter it in both boxes here.
            </p>
          ) : (
            <Fragment>
              {haveExisting ? (
                <p>Select sign-in method:</p>
              ) : (
                <p>
                  Please tell us how you would like to sign in in the future.
                </p>
              )}
              <Field>
                <Control>
                  <Radio
                    name="no-password-option"
                    value={false}
                    checked={!noPassword}
                    onChange={(e) => setNoPasswordWithClearing(false)}
                    disabled={!!loggingIn}
                  >
                    {" "}
                    Use a password to sign in. (If you forget your password, you
                    can always ask for a reset code to be e-mailed to you).
                  </Radio>
                </Control>
              </Field>
              {existing.password ? (
                <Field>
                  To change your password, enter your new password in the two
                  boxes below. To keep your existing password, leave them blank.
                </Field>
              ) : null}
            </Fragment>
          )}
        </Fragment>
      </Content>
      <Field horizontal className="large-horiz-label">
        <Field.Label>
          <Label>{existing.password ? "New " : "Choose "} Password</Label>
        </Field.Label>
        <Field.Body>
          <Field>
            <Control>
              <TrimmedInput
                type="password"
                value={newPassword}
                setValue={setNewPassword}
                disabled={loggingIn || noPassword}
                setFinished={setNewPasswordEntered}
                {...(newPasswordError ? { color: "danger" } : {})}
              />
            </Control>
            {newPasswordError ? (
              <Help color="danger">{newPasswordError}</Help>
            ) : null}
          </Field>
        </Field.Body>
      </Field>

      <Field horizontal className="large-horiz-label">
        <Field.Label>
          <Label>Confirm {existing.password ? "New " : ""} Password</Label>
        </Field.Label>
        <Field.Body>
          <Field>
            <Control>
              <TrimmedInput
                type="password"
                value={newPasswordConfirmation}
                setValue={setNewPasswordConfirmation}
                disabled={loggingIn || noPassword}
                setFinished={setNewPasswordConfirmed}
                {...(newPasswordConfirmationError ? { color: "danger" } : {})}
              />
            </Control>
            {newPasswordConfirmationError ? (
              <Help color="danger">{newPasswordConfirmationError}</Help>
            ) : null}
          </Field>
        </Field.Body>
      </Field>
      {resettingPassword ? (
        <Field>
          <Control>
            <Checkbox
              checked={noPassword}
              onChange={(e) => setNoPasswordWithClearing(e.target.checked)}
              disabled={!!loggingIn}
            >
              {" "}
              Instead of using a password, in future, have a verification code
              sent by e-mail each time.
            </Checkbox>
          </Control>
        </Field>
      ) : (
        <Field>
          <Control>
            <Radio
              name="no-password-option"
              value={true}
              checked={noPassword}
              onChange={(e) => setNoPasswordWithClearing(true)}
              disabled={!!loggingIn}
            >
              {" "}
              Instead of using a password, have a verification code sent by
              e-mail each time.
            </Radio>
          </Control>
        </Field>
      )}
    </div>
  );
}

export function EditAccountSettings() {
  useSetTitle("Sign-in Settings");

  const { data: profileData } = useSelector(profileSlice.selector);

  const changeState = useSelector(changePasswordSlice.selector);
  const haveNoPassword = profileData.no_password;

  const haveExisting = {
    password: !haveNoPassword,
    noPassword: haveNoPassword,
  };

  const promptNewPasswordState = usePromptNewPasswordState(haveExisting);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(changePasswordSlice.actions.clear());
  }, [dispatch]);

  const { personId, authenticatedAsStaff } = usePortalContext();
  const startedSaving = changeState.sendingFor === personId;
  const saving = startedSaving && !changeState.sent;
  const failure =
    startedSaving && changeState.sent ? changeState.failure : null;
  const saved = startedSaving && changeState.sent && !failure;

  const {
    newPasswordIncomplete,
    newPasswordConfirmationIncomplete,
    newPasswordMismatch,
    newPasswordLoginData,
    newPassword,
    noPassword,
  } = promptNewPasswordState;

  const dataToSave = { person_id: personId, ...newPasswordLoginData };
  const changed =
    noPassword !== haveExisting.noPassword
      ? "Settings Change"
      : !noPassword && newPassword !== ""
      ? "New Password"
      : null;
  const incomplete =
    newPasswordIncomplete ??
    newPasswordConfirmationIncomplete ??
    newPasswordMismatch;

  const doClose = useReturn();
  const doSave = () => {
    dispatch(changePasswordSlice.asyncActions.send(dataToSave));
  };

  return (
    <Container className="spaced">
      {authenticatedAsStaff ? (
        <Message color="warning">
          <Message.Body>
            These settings may not be altered while signed in as a staff member;
            they can only be altered by the person themselves when signed in to
            their account.
          </Message.Body>
        </Message>
      ) : null}
      <Message>
        <Message.Header>
          Sign-in Settings
          <Button remove onClick={doClose} />
        </Message.Header>
        <Message.Body className="account-edit">
          {failure ? (
            <ErrorMessage header="Failed to Save Settings">
              {failure.message}
            </ErrorMessage>
          ) : null}
          {saved ? (
            <Message color="success">
              <Message.Body>Changes successfully saved.</Message.Body>
            </Message>
          ) : null}
          {saving ? null : (
            <PromptNewPassword
              loggingIn={saving}
              {...{ haveExisting }}
              {...promptNewPasswordState}
            />
          )}
          <div className="buttons is-centered">
            {changed ? (
              <IconButton
                icon={FaCheck}
                onClick={doSave}
                disabled={saving || !!incomplete}
              >
                Save {changed}
              </IconButton>
            ) : null}
            <IconButton icon={FaTimes} onClick={doClose}>
              Cancel & Close
            </IconButton>
          </div>
        </Message.Body>
      </Message>
    </Container>
  );
}

export default EditAccountSettings;
